import {FC} from 'react';
import {styled} from 'styled-components';

import {UnthemedNavLink} from '@shared-web/components/core/button';
import {SvgIcon} from '@shared-web/components/core/svg_icon';
import {chevronLeftIcon} from '@shared-web/components/icons/chevron_left_icon';
import {chevronRightIcon} from '@shared-web/components/icons/chevron_right_icon';

import {getScoreInfo} from '@src/components/score';
import {ParsedDb} from '@src/lib/db_store';
import {ParsedProject} from '@src/lib/projects_stores';

interface NameNavBarProps {
  db: ParsedDb;
  nameKey: string;
  pathPrefix: string;
  project?: ParsedProject;
}

export const NameNavBar: FC<NameNavBarProps> = props => {
  const {db, nameKey, pathPrefix, project} = props;
  const index = db.findIndex(p => p.key === nameKey);

  const previous = db[index - 1];
  const previousKey = previous?.key;
  const previousName = previous?.totalByName[0]?.name;
  const previousScore = project?.scores[previousKey ?? ''];
  const previousScoreInfo = previousScore !== undefined ? getScoreInfo(previousScore) : undefined;

  const next = db[index + 1];
  const nextKey = next?.key;
  const nextName = next?.totalByName[0]?.name;
  const nextScore = project?.scores[nextKey ?? ''];
  const nextScoreInfo = nextScore !== undefined ? getScoreInfo(nextScore) : undefined;

  return (
    <Wrapper>
      <StyledNavLink
        $visible={previousKey !== undefined}
        $align="left"
        to={`${pathPrefix}${previousKey}`}
      >
        <SvgIcon icon={chevronLeftIcon} color="#ffffff" size="0.7rem" />
        <div>{previousName}</div>
        {previousScoreInfo !== undefined ? (
          <NavLinkScore $color={previousScoreInfo.color}>{previousScoreInfo.label}</NavLinkScore>
        ) : (
          <></>
        )}
      </StyledNavLink>
      <NavTitle>{`${(index + 1).toLocaleString()} / ${db.length.toLocaleString()}`}</NavTitle>
      <StyledNavLink $visible={nextKey !== undefined} $align="right" to={`${pathPrefix}${nextKey}`}>
        {nextScoreInfo !== undefined ? (
          <NavLinkScore $color={nextScoreInfo.color}>{nextScoreInfo.label}</NavLinkScore>
        ) : (
          <></>
        )}
        <div>{nextName}</div>
        <SvgIcon icon={chevronRightIcon} color="#ffffff" size="0.7rem" />
      </StyledNavLink>
    </Wrapper>
  );
};

NameNavBar.displayName = 'NameNavBar';

const Wrapper = styled.div`
  width: 100%;
  height: 3rem;
  color: #ddd;
  display: flex;
  align-items: center;
`;

const NavTitle = styled.div`
  text-align: center;
  flex-shrink: 0;
  flex-grow: 0;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledNavLink = styled(UnthemedNavLink)<{$visible: boolean; $align: 'left' | 'right'}>`
  width: 0;
  flex-grow: 1;
  flex-shrink: 0;
  flex-basis: 1px;

  display: flex;
  align-items: center;
  justify-content: ${p => (p.$align === 'left' ? 'flex-start' : 'flex-end')};
  gap: 0.5rem;

  visibility: ${p => (p.$visible ? 'visible' : 'hidden')};
  padding: 0 0.5rem;
  font-size: 1.1rem;
`;

const NavLinkScore = styled.div<{$color: string}>`
  background-color: ${p => p.$color}aa;
  padding: 0.2rem 0.5rem;
  font-size: 0.8rem;
  border-radius: 1000rem;
  color: #ddd;
`;
